<template>
	<ho-form class="form-gray mt-4" v-model="formData" :disableAutocomplete="true" @onOk="handleSave"
		@onCancel="handleCancel" :okButtonText="loading ? localization.t('common.saving') : localization.t('common.save')"
		:cancelButtonText="localization.t('common.cancel')" :disableOk="!isValid">
		<template v-slot:body>
			<div class="form-row" v-if="editMode">
				<div class="form-cell">
					<toggle class="mt-2 ml-0" :label="localization.t('administration.organizations.form.active')"
						name="organization-active" :checked="organizationData?.active" @onChange="handleActiveChange" />
					<p v-if="activeWarningKey" class="text-orange-600 mt-2">{{ localization.t(activeWarningKey) }}</p>
				</div>
			</div>
			<div class="form-row">
				<div class="form-cell flex-1">
					<ho-text :label="localization.t('administration.organizations.form.name')" v-model="formData.name"
						:disable-autocomplete="true" :validation="validators.name" modelKey="name"
						@onValidStateChange="handleValidationStateChange" />
				</div>
			</div>
			<div class="form-row">
				<div class="form-cell flex-none">
					<ho-select :label="localization.t('administration.organizations.form.plan')" v-model="formData.planName"
						modelKey="planName" :select-options-list="planOptions" />
				</div>
			</div>
			<div class="form-row">
				<div class="form-cell flex-none">
					<ho-text :label="localization.t('administration.organizations.form.max-companies')"
						v-model="formData.maxCompanies" :disable-autocomplete="true" :validation="validators.maxCompanies"
						modelKey="maxCompanies" @onValidStateChange="handleValidationStateChange" />
				</div>
			</div>
		</template>
	</ho-form>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import HoForm from '@/components/form/ho-form.vue';
import HoText from '@/components/form/ho-text.vue';
import HoSelect from '@/components/form/ho-select.vue';
import Toggle from '@/components/form/toggle.vue';
import { PLAN_NAMES } from '@/constants/organization';
import IOrganization, { ICreateOrganizationData, IUpdateOrganizationData } from '@/interfaces/organizations/iOrganization';
import { getValidators, ValidationField } from './validations';
import { getActiveChangeWarningKey } from './utils';

const localization = useI18n();

const validators = getValidators(localization.t);

const props = defineProps({
	loading: Boolean,
	editMode: Boolean,
	organizationData: {
		type: Object as () => IOrganization,
	},
});

const emit = defineEmits(['onClose']);

const planOptions = PLAN_NAMES.map((p) => ({ label: p, value: p }));

const formData = ref({
	name: props.organizationData?.name ?? '',
	planName: props.organizationData?.planName ?? PLAN_NAMES[0],
	maxCompanies: (props.organizationData?.maxCompanies ?? '') as string | number,
	active: props.organizationData?.active ?? true,
});

const validState = ref<{ [key in ValidationField]: boolean }>({
	name: !!props.organizationData?.name,
	maxCompanies: !!props.organizationData?.maxCompanies,
});

const activeWarningKey = computed(() =>
	props.editMode && props.organizationData
		? getActiveChangeWarningKey(props.organizationData.active, formData.value.active)
		: null
);

const isValid = computed(() => !props.loading && validState.value.name && validState.value.maxCompanies);

const handleCancel = () => { emit('onClose'); };

const handleSave = () => {
	const data: ICreateOrganizationData = {
		name: formData.value.name.trim(),
		planName: formData.value.planName,
		maxCompanies: Number(formData.value.maxCompanies),
	};

	if (props.editMode && props.organizationData) {
		const updateData: IUpdateOrganizationData = { ...data, id: props.organizationData.id, active: formData.value.active };
		emit('onClose', updateData);
		return;
	}

	emit('onClose', data);
};

const handleActiveChange = (value: boolean) => {
	formData.value.active = value;
};

const handleValidationStateChange = (fieldKey: ValidationField, value: boolean) => {
	validState.value[fieldKey] = value;
};
</script>

<style lang="postcss" scoped>
.form-gray {
	.form-row {
		.form-cell {
			margin-left: 0;
		}
	}
}

.form-row .form-cell label.ho-toggle-component {
	display: flex;
}
</style>
